// This app's solution slug on the shared central identity provider, and the
// names of every group and tenant role it honors. Shared by the backend
// (lib/solution.mjs re-exports it) and the SPA, so the two can never
// disagree on a role name.
//
// The slug is the name of this app's central app client, the prefix of
// every group and tenant role this app honors (`<solution>-<role>`, Hugo
// Andrade 2026-09-24), and the `app` value sent on member creation so the
// central IdP brands the invite email with a link to this portal. Single
// source: never repeat the literal elsewhere.
export const SOLUTION_SLUG = 'onguard-entraid';

// Every name this app honors starts with this; a bare `admin`, a
// `tenant-admin`, or another solution's role never matches.
export const ROLE_PREFIX = `${SOLUTION_SLUG}-`;

// Staff group (cognito:groups), IPC Global SSO sign-ins only (lib/auth.mjs).
export const OPERATORS_GROUP = `${SOLUTION_SLUG}-operators`;

// Tenant membership roles (tenant_roles of the active tenant). They combine,
// and neither of the last two implies admin (Hugo Andrade, 2026-09-29):
//   - admin: everything a customer admin has today, including People.
//   - developer: read-only Data reference of that customer, nothing else.
//   - qlik-credentials: the customer's Qlik Cloud section and its Reveal
//     credentials action, nothing else.
export const ADMIN_ROLE = `${SOLUTION_SLUG}-admin`;
export const DEVELOPER_ROLE = `${SOLUTION_SLUG}-developer`;
export const QLIK_CREDENTIALS_ROLE = `${SOLUTION_SLUG}-qlik-credentials`;
// auditor: read-only Compliance reports and controls of that customer, only
// while a non-expired, non-revoked grant exists (lib/compliance-auth.mjs).
export const AUDITOR_ROLE = `${SOLUTION_SLUG}-auditor`;
// viewer: read-only Overview and Security of that customer (scores, KPIs, By
// area, every check and its details), nothing else (Hugo Andrade, 2026-10-25).
// Deny by default: a route admits it only through an explicit allowRoles entry
// (lib/auth.mjs), pinned by tests/viewer-deny-by-default.test.mjs.
export const VIEWER_ROLE = `${SOLUTION_SLUG}-viewer`;
// reviewer: Access Reviews "Your reviews" of that customer (GET mine, POST mine
// decide, POST mine delegate) for the caller's own reviewer items, nothing
// else (spec 2026-10-29 section 2). Honored only while REVIEWER_ROLE_REGISTERED
// is exactly 'true'. Given by the Access Reviews engine when a person becomes
// a reviewer and kept after the review closes, until an admin removes it on
// the People page; never offered there (PEOPLE_GRANTABLE_ROLES). Deny by default like the viewer: a route admits it
// only on purpose (routes/customer-access-reviews.mjs).
export const ACCESS_REVIEWER_ROLE = `${SOLUTION_SLUG}-reviewer`;

// The only tenant roles this app recognizes. The operators group is never
// among them. People may grant only PEOPLE_GRANTABLE_ROLES; a held reviewer
// role is still listed and kept (role merges never drop it).
export const TENANT_ROLES = Object.freeze([ADMIN_ROLE, DEVELOPER_ROLE, QLIK_CREDENTIALS_ROLE, AUDITOR_ROLE, VIEWER_ROLE, ACCESS_REVIEWER_ROLE]);

// The roles the People page offers. The Access Reviews reviewer role is not
// among them: Access Reviews gives it; People may only remove a held one.
export const PEOPLE_GRANTABLE_ROLES = Object.freeze(TENANT_ROLES.filter((r) => r !== ACCESS_REVIEWER_ROLE));

// What People shows for each role (English only, no role name leaks into UI copy).
export const TENANT_ROLE_LABELS = Object.freeze({
  [ADMIN_ROLE]: 'Admin',
  [DEVELOPER_ROLE]: 'Developer',
  [QLIK_CREDENTIALS_ROLE]: 'Qlik credentials',
  [AUDITOR_ROLE]: 'Auditor',
  [VIEWER_ROLE]: 'Viewer',
  [ACCESS_REVIEWER_ROLE]: 'Access Reviews reviewer',
});

export const TENANT_ROLE_DESCRIPTIONS = Object.freeze({
  [ADMIN_ROLE]: 'Everything in this portal for this customer, including People.',
  [DEVELOPER_ROLE]: 'Read-only Data reference. Nothing else.',
  [QLIK_CREDENTIALS_ROLE]: 'The Qlik Cloud section and its Reveal credentials action. Nothing else.',
  [AUDITOR_ROLE]: 'Read-only Compliance reports and controls until access expires. Nothing else.',
  [VIEWER_ROLE]: 'Read-only Overview and Security. Nothing else.',
  [ACCESS_REVIEWER_ROLE]: 'Their own assignments in an open access review. Given when they become a reviewer and kept until you remove it here. Nothing else.',
});

export const isTenantRole = (role) => typeof role === 'string' && TENANT_ROLES.includes(role);
